import { useMemo } from 'react';
import { PAYMENT_CONFIG } from 'state/paymentConfig/type';
import { CONFIG_CODES } from 'constants/settings/internalConfig';
import { getStatusOptionsFromMapper } from 'utils/settings/internalConfig';
import { ProcessorWebhookConfig, StatusMapper } from 'types/settings/internalConfig';

interface UseWebhookConfigsProps {
  processors: PAYMENT_CONFIG.IProcessors[] | undefined | null;
  cardReaderValues: Array<PAYMENT_CONFIG.IGetCardReaderValues> | undefined;
  processorStatusMappers: Record<string, StatusMapper | null>;
}

export const useWebhookConfigs = ({
  processors,
  cardReaderValues,
  processorStatusMappers,
}: UseWebhookConfigsProps): ProcessorWebhookConfig[] => {
  // Filter MOCK processors
  const mockProcessors = useMemo(() => {
    if (!processors) return [];

    return processors.filter(
      (processor) =>
        processor.isEnabled &&
        processor.processorName?.toUpperCase().startsWith('MOCK')
    );
  }, [processors]);

  // Build webhook configs
  const webhookConfigs = useMemo<ProcessorWebhookConfig[]>(() => {
    if (mockProcessors.length === 0) return [];

    return mockProcessors
      .filter((processor) => {
        // Check if this processor has bypassWebhookCallback config
        const hasBypassConfig = cardReaderValues?.some(
          (item) =>
            item.code === CONFIG_CODES.BYPASS_WEBHOOK_CALLBACK &&
            item.processorId === processor.processorId
        );

        // Check if this processor has webhookBypassStatus config
        const hasStatusConfig = cardReaderValues?.some(
          (item) =>
            item.code === CONFIG_CODES.WEBHOOK_BYPASS_STATUS &&
            item.processorId === processor.processorId
        );

        // Only show processors that have BOTH configs
        return hasBypassConfig && hasStatusConfig;
      })
      .map((processor) => {
        const bypassConfig = cardReaderValues?.find(
          (item) =>
            item.code === CONFIG_CODES.BYPASS_WEBHOOK_CALLBACK &&
            item.processorId === processor.processorId
        );

        const statusConfig = cardReaderValues?.find(
          (item) =>
            item.code === CONFIG_CODES.WEBHOOK_BYPASS_STATUS &&
            item.processorId === processor.processorId
        );

        const statusOptions = getStatusOptionsFromMapper(
          processorStatusMappers[processor.processorId] || null
        );

        return {
          processorId: processor.processorId,
          processorName: processor.processorName || '',
          bypassEnabled: bypassConfig?.value === 'true',
          webhookStatus: statusConfig?.value || '',
          statusOptions,
        };
      });
  }, [mockProcessors, cardReaderValues, processorStatusMappers]);

  return webhookConfigs;
};
